import { Button } from "@mui/material";
import {
  Undo20Px,
  ConfirmationModal,
  mainColours,
  useToast
} from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import {
  mixpanelTrack,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { fetchPortStatus, NextEmptyBinSuccessAction } from "~/redux/actions";
import {
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import {
  useNextEmptyBinMutation,
  usePostGetPreviousBinMutation
} from "~/redux/warehouse/autostoreGrid.hooks";

import {
  selectCompartment,
  selectIsPreviousBinFlow,
  selectPreviousPutawayTask,
  setPreviousBinNumber
} from "./autostorePutaway.slice";

export function GetPreviousBinButton() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Previous Bin");

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLoadingPreviousBin, setIsLoadingPreviousBin] = useState(false);

  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const workstationId = useAppSelector(selectWorkstationId);
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);

  const isButtonDisabled = !previousPutawayTask || isPreviousBinFlow;

  const [postGetPreviousBin] = usePostGetPreviousBinMutation();
  const [getNextEmptyBin] = useNextEmptyBinMutation();

  const handleGetPreviousBin = async () => {
    if (!autostoreGridId || !workstationId || !previousPutawayTask) {
      return errorToast(t("error getting previous bin"), {
        trackingLocation: trackedModalName
      });
    }
    try {
      const { binId, portId } = previousPutawayTask;
      dispatch(setPreviousBinNumber(binId));

      await postGetPreviousBin({
        autostoreGridId,
        portId,
        binNumber: binId
      }).unwrap();

      const nextEmptyBin = await getNextEmptyBin({
        autostoreGridId,
        portId
      }).unwrap();
      dispatch<NextEmptyBinSuccessAction>({
        type: "autostore/NEXT_EMPTY_BIN_SUCCESS",
        payload: {
          nextEmptyBin,
          portId
        }
      });

      dispatch(selectCompartment(previousPutawayTask.compartment));

      await dispatch(fetchPortStatus({ portId }));
    } catch (e) {
      errorToast(getMessageFromRtkError(e), {
        trackingLocation: trackedModalName
      });
    }
  };

  const onConfirmGetPreviousBin = async () => {
    setIsLoadingPreviousBin(true);
    await handleGetPreviousBin();
    setIsConfirmationModalOpen(false);
    setIsLoadingPreviousBin(false);
  };

  const confirmationModalText = (
    <span>
      {t("are you sure you want to get the previous bin")}
      <br />
      <br />
      {t("get previous bin modal note")}
    </span>
  );

  return (
    <>
      <Button
        variant="subtle"
        size="large"
        disabled={isButtonDisabled}
        onClick={() => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Get Previous Bin",
            eventProperties: {
              BinId: previousPutawayTask?.binId
            }
          });
          setIsConfirmationModalOpen(true);
        }}
        startIcon={
          <Undo20Px
            fill={isButtonDisabled ? mainColours.text.disabled : "inherit"}
          />
        }
      >
        {t("get previous bin")}
      </Button>

      <ConfirmationModal
        modalTitle={t("previous bin")}
        modalText={confirmationModalText}
        isOpen={isConfirmationModalOpen}
        confirmCb={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Confirm",
            eventProperties: {
              BinId: previousPutawayTask?.binId,
              Compartment: previousPutawayTask?.compartment,
              PortId: previousPutawayTask?.portId
            }
          });
          void onConfirmGetPreviousBin();
        }}
        closeCb={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Cancel"
          });
          setIsConfirmationModalOpen(false);
        }}
        disableConfirmButton={isLoadingPreviousBin}
        labelledById="get-previous-bin-modal-title"
      />
    </>
  );
}

export default GetPreviousBinButton;
